import { useEffect, useRef, useState } from 'react';
import { Bell } from 'lucide-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { useInbox, useMarkRead, useUnreadNotifications, type InboxItem } from './api';

dayjs.extend(relativeTime);

/**
 * TopBar bell. Badge shows unread count, dropdown lists the last ~20 inbox
 * items. Click an unread item to navigate (placeholder for now) and mark read.
 */
export function NotificationBell() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { data: unread } = useUnreadNotifications();
  const inbox = useInbox({ page: 1, limit: 20 });
  const markRead = useMarkRead();
  const unreadCount = (unread ?? []).length;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={t('notifications.bell.title')}
        aria-label={t('notifications.bell.title')}
        className={cn(
          'relative grid h-7 w-7 place-items-center rounded-md transition-colors',
          'text-[var(--color-xk-text-muted)] hover:bg-[var(--color-xk-surface)] hover:text-[var(--color-xk-lavender)]',
          open && 'bg-[var(--color-xk-surface)] text-[var(--color-xk-lavender)]',
        )}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span
            aria-hidden="true"
            className="absolute -right-1 -top-1 grid h-4 min-w-[16px] place-items-center rounded-full bg-[var(--color-xk-accent)] px-1 text-[9px] font-extrabold text-[#0A0A0A]"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] z-50 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] shadow-[0_12px_32px_rgba(0,0,0,0.4)]">
          <header className="flex items-center justify-between border-b border-[var(--color-xk-border)] px-4 py-2.5">
            <span
              className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {t('notifications.bell.title')}
            </span>
            <span className="text-[10px] text-[var(--color-xk-text-muted)]">
              {unreadCount > 0
                ? t('notifications.bell.unreadCount', { count: unreadCount })
                : t('notifications.bell.allCaughtUp')}
            </span>
          </header>

          <ul className="max-h-[420px] overflow-y-auto scrollbar-thin">
            {inbox.isLoading && (
              <li className="px-4 py-6 text-center text-[12px] text-[var(--color-xk-text-muted)]">
                {t('common.loading')}
              </li>
            )}
            {!inbox.isLoading && (inbox.data?.items ?? []).length === 0 && (
              <li className="px-4 py-8 text-center text-[12px] text-[var(--color-xk-text-muted)]">
                {t('notifications.bell.empty')}
              </li>
            )}
            {(inbox.data?.items ?? []).map((item) => (
              <BellRow
                key={item.id}
                item={item}
                onOpen={() => {
                  if (!item.readAt) {
                    markRead.mutate({ id: item.notificationId, source: 'inapp' });
                  }
                  setOpen(false);
                }}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function BellRow({ item, onOpen }: { item: InboxItem; onOpen: () => void }) {
  const sender = item.notification.sender;
  const senderName =
    `${sender?.firstname ?? ''} ${sender?.lastname ?? ''}`.trim() || sender?.email || '—';
  const initial = (sender?.firstname?.[0] ?? sender?.email?.[0] ?? '?').toUpperCase();
  const isUnread = !item.readAt;
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          'flex w-full items-start gap-2.5 border-b border-[var(--color-xk-border)] px-4 py-3 text-left transition-colors hover:bg-[var(--color-xk-surface)]',
          isUnread && 'bg-[color-mix(in_srgb,var(--color-xk-accent)_06%,transparent)]',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'grid h-8 w-8 flex-none place-items-center rounded-full text-[11px] font-extrabold',
            isUnread
              ? 'bg-[var(--color-xk-accent)] text-[#0A0A0A]'
              : 'bg-[var(--color-xk-surface)] text-[var(--color-xk-lavender)]',
          )}
        >
          {initial}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-[12px] font-extrabold text-[var(--color-xk-lavender)]">
              {item.notification.title}
            </span>
            <span className="flex-none text-[10px] text-[var(--color-xk-text-muted)]">
              {dayjs(item.notification.createdAt).fromNow(true)}
            </span>
          </div>
          <p className="mt-0.5 line-clamp-2 whitespace-pre-line text-[11px] text-[var(--color-xk-lavender)]/80">
            {item.notification.body}
          </p>
          <p className="mt-0.5 truncate text-[10px] text-[var(--color-xk-text-muted)]">
            {senderName}
          </p>
        </div>
        {isUnread && (
          <span
            aria-hidden="true"
            className="mt-1 h-2 w-2 flex-none rounded-full bg-[var(--color-xk-accent)]"
          />
        )}
      </button>
    </li>
  );
}
